import { useState } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  CircularProgress,
  Collapse,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { DateTime, Settings } from 'luxon';
import { AxiosError } from 'axios';
import { Money } from '../../../utils/currency';
import { BillingItem } from '../../../model/BillingItem';
import { Invoice, InvoiceStatus } from '../../../model/Invoice';
import { useAuth } from '../../../stores';
import { useBillingItemsQuery } from '../../../stores/FetcherStores/BillingItemFetcher';
import {
  useInvoicesQuery,
  useGenerateCompanyInvoiceMutation,
  useSendInvoiceToFortnoxMutation,
} from '../../../stores/FetcherStores/InvoiceFetcher';
import BillingItemForm from './BillingItemForm';
import useCompanyId from '../../../hooks/useCompanyId';
import {
  filterBillingItemsForMonth,
  getBillingItemProratedPrice,
} from '../../../utils/billingItem';
import InvoiceRow from './InvoiceRow';

Settings.defaultLocale = 'sv-SE';

const Invoicing = observer(() => {
  const companyId = useCompanyId();
  const auth = useAuth();

  const { data: billingItems, refetch, isLoading } = useBillingItemsQuery(companyId || '');
  const {
    data: invoices,
    refetch: refetchInvoices,
    isLoading: invoicesLoading,
  } = useInvoicesQuery(companyId || '');
  const {
    mutateAsync: generateInvoice,
    isLoading: isGeneratingInvoice,
    error: generateInvoiceError,
  } = useGenerateCompanyInvoiceMutation();
  const {
    mutateAsync: sendToFortnox,
    isLoading: isSendingToFortnox,
    error: sendToFortnoxError,
  } = useSendInvoiceToFortnoxMutation();

  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editingBillingItemId, setEditingBillingItemId] = useState<string | undefined>(undefined);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | undefined>(undefined);

  const monthString = DateTime.now().toFormat('yyyy-MM');
  const filteredBillingItems = filterBillingItemsForMonth(billingItems || [], monthString);

  const toggleInvoiceExpansion = (invoiceId: string) => {
    setExpandedInvoiceId(expandedInvoiceId === invoiceId ? undefined : invoiceId);
  };

  const formatTimestamp = (date: Date | null | undefined) => {
    if (!date) return '-';
    return DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_SHORT);
  };

  const currentMonth = DateTime.now().startOf('month').plus({ months: 1 }).toFormat('yyyy-MM-dd');
  const calculateInvoiceTotal = (invoice: Invoice) => {
    return invoice.invoiceRows.reduce(
      (total, row) =>
        total.add(row.unitPrice.applyPercentageDiscount(row.discount).multiply(row.quantity)),
      new Money(0),
    );
  };

  const formatPeriod = (period: string) => {
    if (!period || !period.match(/^\d{4}-\d{2}$/)) return period;
    const [year, month] = period.split('-');
    const date = DateTime.fromObject({ year: parseInt(year, 10), month: parseInt(month, 10) });
    return date.toFormat('MMMM yyyy');
  };

  const handleEditBillingItem = (item: BillingItem) => {
    setEditingBillingItemId(item.id);
    setAddFormOpen(true);
  };

  const handleCloseForm = () => {
    setAddFormOpen(false);
    setEditingBillingItemId(undefined);
  };

  const currentMonthSum = filteredBillingItems?.reduce((acc: Money, item: BillingItem) => {
    const price = getBillingItemProratedPrice(item, monthString);
    return acc.add(price);
  }, new Money(0));

  const error = generateInvoiceError || sendToFortnoxError;
  const errorMessage =
    error instanceof AxiosError ? error?.response?.data?.name || error?.message : error?.message;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h1" gutterBottom>
          Fakturering
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {auth.isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddFormOpen(true)}
            >
              Lägg till faktureringspost
            </Button>
          )}
          {auth.isAdmin && (
            <Button
              variant="outlined"
              onClick={() => generateInvoice(companyId, monthString)}
              disabled={!companyId}
              color="secondary"
            >
              {isGeneratingInvoice ? 'Genererar...' : 'Generera faktura'}
            </Button>
          )}
          <IconButton
            aria-label="refresh"
            size="small"
            onClick={() => refetch()}
            sx={{ ml: 1 }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : <RefreshRounded />}
          </IconButton>
        </Box>
      </Box>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {/* Current Month Preview Table */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Nästa faktura {currentMonth} (förhandsvisning)
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Namn</TableCell>
              <TableCell>Startdatum</TableCell>
              <TableCell>Slutdatum</TableCell>
              <TableCell align="right">Antal</TableCell>
              <TableCell align="right">Pris per enhet</TableCell>
              <TableCell align="right">Totalt (prorat)</TableCell>
              {auth.isAdmin && <TableCell align="center">Åtgärder</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBillingItems?.map((item: BillingItem) => (
              <InvoiceRow
                key={`billing-item-${item.type}-${item.id || 'no-id'}`}
                billingItem={item}
                handleEditBillingItem={handleEditBillingItem}
                month={monthString}
              />
            ))}
            <TableRow>
              <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold' }}>
                Summa {currentMonth}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {currentMonthSum?.format()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Historical Invoices Section */}
      <Typography variant="h5" gutterBottom sx={{ mt: 6, mb: 2 }}>
        Historiska fakturor
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Period</TableCell>
              <TableCell>Skapad</TableCell>
              <TableCell>Fakturadatum</TableCell>
              <TableCell align="right">Antal rader</TableCell>
              <TableCell align="right">Totalt belopp</TableCell>
              {auth.isAdmin && <TableCell align="center">Åtgärder</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices?.map((invoice: Invoice) => (
              <>
                <TableRow key={invoice.id}>
                  <TableCell>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => toggleInvoiceExpansion(invoice.id)}
                    >
                      {expandedInvoiceId === invoice.id ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{formatPeriod(invoice.period)}</TableCell>
                  <TableCell>{formatTimestamp(invoice.createdDate)}</TableCell>
                  <TableCell>{formatTimestamp(invoice.invoiceDate)}</TableCell>
                  <TableCell align="right">{invoice.invoiceRows.length}</TableCell>
                  <TableCell align="right">{calculateInvoiceTotal(invoice).format()}</TableCell>
                  {auth.isAdmin && (
                    <TableCell align="center">
                      {!invoice.status || invoice.status === InvoiceStatus.DRAFT ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => sendToFortnox(companyId, invoice.id)}
                          disabled={isSendingToFortnox}
                          color="secondary"
                        >
                          {isSendingToFortnox ? 'Skickar...' : 'Skicka till Fortnox'}
                        </Button>
                      ) : (
                        'Redan skickad'
                      )}
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={auth.isAdmin ? 8 : 7}
                  >
                    <Collapse in={expandedInvoiceId === invoice.id} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          Fakturarader
                        </Typography>
                        <Table size="small" aria-label="purchases">
                          <TableHead>
                            <TableRow>
                              <TableCell>Beskrivning</TableCell>
                              <TableCell align="right">Antal</TableCell>
                              <TableCell align="right">Pris per enhet</TableCell>
                              <TableCell align="right">Totalt</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {invoice.invoiceRows.map((row) => (
                              <TableRow key={row.title}>
                                <TableCell component="th" scope="row">
                                  {row.title}
                                </TableCell>
                                <TableCell align="right">{row.quantity}</TableCell>
                                <TableCell align="right">
                                  {row.unitPrice.applyPercentageDiscount(row.discount).format()}
                                </TableCell>
                                <TableCell align="right">
                                  {row.unitPrice
                                    .applyPercentageDiscount(row.discount)
                                    .multiply(row.quantity)
                                    .format()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
            {(!invoices || invoices.length === 0) && (
              <TableRow>
                <TableCell colSpan={auth.isAdmin ? 8 : 7} align="center">
                  {invoicesLoading ? <CircularProgress size={24} /> : 'Inga fakturor hittades'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton
            aria-label="refresh invoices"
            size="small"
            onClick={() => refetchInvoices()}
            disabled={invoicesLoading}
          >
            {invoicesLoading ? <CircularProgress size={20} /> : <RefreshRounded />}
          </IconButton>
        </Box>
      </TableContainer>

      <BillingItemForm
        open={addFormOpen}
        onClose={handleCloseForm}
        companyId={companyId || ''}
        billingItemId={editingBillingItemId}
      />
    </Box>
  );
});

export default Invoicing;
